import { Group, Modal, Stack, Textarea } from '@mantine/core';
import { IconCheck, IconCopy, IconExternalLink } from '@tabler/icons-react';
import { useFileManager } from 'api/file-manager/file-manager-context';
import { CopyButton } from '@mantine/core';
import panic from 'errors/panic';
import { useEffect, useState } from 'react';
import P2Regular from 'components/typography/P2Regular';
import Button from 'components/Button';

interface FileDownloadLinkModalProps {
  fileId: string | null;
  opened: boolean;
  onClose: () => void;
}

/**
 * Modal that generates a time-limited download link and displays it for copying or opening.
 */
export default function FileDownloadLinkModal({ fileId, opened, onClose }: FileDownloadLinkModalProps) {
  const { getFileMetadata, createTimeAccessToken, buildTimeAccessFileUrl } = useFileManager();
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!opened || !fileId) {
      setUrl(null);
      return;
    }

    setLoading(true);
    setUrl(null);

    Promise.all([createTimeAccessToken({ fileId, validForMinutes: 15 }), getFileMetadata({ fileId })])
      .then(([accessToken, { basename }]) => {
        setUrl(buildTimeAccessFileUrl({ token: accessToken, fileName: basename }));
      })
      .catch(panic)
      .finally(() => setLoading(false));
  }, [opened, fileId, createTimeAccessToken, getFileMetadata, buildTimeAccessFileUrl]);

  return (
    <Modal title="Bezpečný odkaz na stiahnutie" opened={opened} onClose={onClose} centered size={500}>
      <Stack spacing={16} pt={16}>
        <Stack spacing={8}>
          <P2Regular>
            Vygenerovali sme unikátny odkaz platný 15 minút, pomocou ktorého môžete bezpečne stiahnuť súbor.
          </P2Regular>
          <Textarea autosize value={loading ? 'Generujem odkaz…' : url ?? ''} readOnly />
        </Stack>
        {url && (
          <Group spacing={8}>
            <CopyButton value={url}>
              {({ copied, copy }) => (
                <Button
                  variant="secondary"
                  size="md"
                  leftIcon={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                  onClick={copy}
                >
                  {copied ? 'Skopírované' : 'Kopírovať odkaz'}
                </Button>
              )}
            </CopyButton>
            <Button
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              variant="secondary"
              size="md"
              leftIcon={<IconExternalLink size={16} />}
            >
              Otvoriť v novom okne
            </Button>
          </Group>
        )}
      </Stack>
    </Modal>
  );
}
